/**
 * Профиль — страница `/me` (docs/10-ui-ux.md).
 *
 * Данные аккаунта, способы входа и смена пароля (FR-A6, FR-A7, FR-A13).
 * Прогресс живёт на своей странице `/progress`: сюда заходят раз в полгода
 * сменить пароль, а на прогресс — после каждой решённой задачи, и держать
 * их вместе значило заставлять пролистывать настройки ради счётчика.
 */

import { type FormEvent, useState } from 'react'

import { useChangePassword, useCurrentUser } from '@/api/auth'
import { useLoginMethods } from '@/api/oauth'
import { strings } from '@/lib/strings'

import { Field } from './AuthForm'
import { LoginMethodsSection } from './LoginMethodsSection'
import { authErrorMessage } from './errorMessage'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function ProfilePage() {
  const { data: user } = useCurrentUser()
  const methods = useLoginMethods()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const mutation = useChangePassword()

  // Пока способы входа не загружены, считаем, что пароль есть: так форма
  // не мигает с «Задать пароль» на «Сменить пароль» у обычного аккаунта.
  const hasPassword = methods.data?.has_password ?? true

  if (!user) {
    return null
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    mutation.mutate(
      // У аккаунта без пароля подтверждать нечем (FR-A13).
      { currentPassword: hasPassword ? currentPassword : undefined, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('')
          setNewPassword('')
          void methods.refetch()
        },
      },
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-12">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">{strings.profile.title}</h1>

        <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted">{strings.auth.email}</dt>
          <dd>{user.email}</dd>
          <dt className="text-muted">{strings.profile.displayName}</dt>
          <dd>{user.display_name ?? strings.profile.noDisplayName}</dd>
          <dt className="text-muted">{strings.profile.registeredAt}</dt>
          <dd>{formatDate(user.created_at)}</dd>
        </dl>

        <h2 className="mt-10 text-lg font-semibold">{strings.profile.loginMethods}</h2>
        <LoginMethodsSection />

        <h2 className="mt-10 text-lg font-semibold">
          {hasPassword ? strings.profile.changePassword : strings.profile.setPassword}
        </h2>
        <p className="mt-1 text-sm text-muted">
          {hasPassword ? strings.profile.changePasswordHint : strings.profile.setPasswordHint}
        </p>

        <form onSubmit={handleSubmit} className="mt-4 flex max-w-sm flex-col gap-4" noValidate>
          {hasPassword && (
            <Field
              id="current-password"
              label={strings.profile.currentPassword}
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              required
              onChange={setCurrentPassword}
            />
          )}
          <Field
            id="new-password"
            label={strings.profile.newPassword}
            type="password"
            value={newPassword}
            autoComplete="new-password"
            required
            hint={strings.auth.passwordRequirements}
            onChange={setNewPassword}
          />

          {mutation.error !== null && (
            <p
              role="alert"
              className="rounded-lg border border-warning/50 p-3 text-sm text-warning"
            >
              {authErrorMessage(mutation.error)}
            </p>
          )}
          {mutation.isSuccess && (
            <p
              role="status"
              className="rounded-lg border border-success/50 p-3 text-sm text-success"
            >
              {strings.profile.passwordChanged}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-accent px-4 py-2.5 font-medium text-accent-fg disabled:opacity-60"
          >
            {mutation.isPending
              ? strings.auth.pending
              : hasPassword
                ? strings.profile.submitPassword
                : strings.profile.submitSetPassword}
          </button>
        </form>
      </section>
    </div>
  )
}
